import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, Modal, Spinner, Badge } from 'reactstrap';
import { ArrowLeft, Save, Plus, Trash2, Edit2, Check, LayoutGrid, X, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSiteStore } from '../../../stores/Assets/site';
import { useSpaceStore } from '../../../stores/Assets/space';
import { useMenuStore } from '../../../stores/Assets/menu';
import { useKitchenStore } from '../../../stores/Assets/kitchen';
import './ManageSpacesView.css';

const THEME_COLOR = '#c59253';

// --- Components from DistributionStep ---

const SpaceCard = ({ name, isNew, onEdit, onDelete, isSelected, onToggle, loading }) => (
    <div
        className={`d-flex align-items-center gap-3 p-2 px-3 rounded-4 shadow-sm transition-all bg-card-theme border position-relative`}
        onClick={!loading ? onToggle : undefined}
        style={{
            borderColor: isSelected ? THEME_COLOR : 'var(--vz-border-color, #f1f5f9)',
            cursor: onToggle && !loading ? 'pointer' : 'default',
            width: 'fit-content',
            minWidth: '140px'
        }}
    >
        <div className="d-flex align-items-center gap-2 flex-grow-1 overflow-hidden">
            {onToggle && (
                <div
                    className={`rounded-circle border d-flex align-items-center justify-content-center transition-all ${isSelected ? 'text-white border-transparent' : 'bg-card-theme'}`}
                    style={{
                        width: 18,
                        height: 18,
                        minWidth: 18,
                        backgroundColor: isSelected ? THEME_COLOR : undefined
                    }}
                >
                    {isSelected && <Check size={10} />}
                </div>
            )}
            <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                <span className="fw-bold text-dark text-truncate" style={{ fontSize: '13px' }}>
                    {name}
                </span>
                {isNew && <Badge color="soft-success" className="bg-success bg-opacity-10 text-success border-0 px-2 py-0 mt-1 w-auto align-self-start" style={{ fontSize: '8px' }}>NEW</Badge>}
            </div>
        </div>

        <div className="d-flex gap-1" onClick={e => e.stopPropagation()}>
            {loading ? (
                <Spinner size="sm" color="secondary" style={{ width: '13px', height: '13px', borderWidth: '2px' }} />
            ) : (
                <>
                    {onEdit && (
                        <Button color="link" className="p-1 text-muted opacity-50 hover-opacity-100" onClick={onEdit}>
                            <Edit2 size={13} />
                        </Button>
                    )}
                    {onDelete && (
                        <Button color="link" className="p-1 text-danger opacity-50 hover-opacity-100" onClick={onDelete}>
                            <Trash2 size={13} />
                        </Button>
                    )}
                </>
            )}
        </div>
    </div>
);

const GenerationView = ({ onCancel, onConfirm, siteName }) => {
    const [count, setCount] = useState(1);
    const [prefix, setPrefix] = useState('Table');

    const handleConfirm = () => {
        const names = [];
        for (let i = 1; i <= count; i++) {
            names.push(`${prefix} ${i}`);
        }
        onConfirm(names);
    };

    return (
        <div className="h-100 d-flex flex-column bg-card-theme rounded-4 overflow-hidden">
            <div className="p-3 border-bottom d-flex align-items-center justify-content-between bg-card-theme sticky-top">
                <div>
                    <h5 className="fw-bold mb-0">Generate Spaces</h5>
                    <p className="text-muted small mb-0">{siteName}</p>
                </div>
                <Button color="link" className="text-muted p-0" onClick={onCancel}><X size={24} /></Button>
            </div>

            <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center p-4">
                <div className="w-100" style={{ maxWidth: '400px' }}>
                    <div className="text-center mb-5">
                        <label className="text-muted fw-bold text-uppercase tracking-wider mb-3 d-block" style={{ fontSize: '12px' }}>How many spaces?</label>
                        <div className="d-flex align-items-center justify-content-center gap-4">
                            <Button
                                color="light"
                                className="rounded-circle d-flex align-items-center justify-content-center border shadow-sm"
                                style={{ width: '50px', height: '50px', fontSize: '24px', backgroundColor: 'var(--vz-light)', borderColor: 'var(--vz-border-color)' }}
                                onClick={() => setCount(Math.max(1, count - 1))}
                            >
                                -
                            </Button>
                            <Input
                                type="number"
                                className="text-center fw-bold border-0 bg-transparent p-0 text-dark"
                                style={{ fontSize: '72px', width: '150px', height: 'auto', outline: 'none', boxShadow: 'none' }}
                                value={count}
                                onChange={(e) => setCount(Math.max(1, parseInt(e.target.value) || 0))}
                            />
                            <Button
                                color="light"
                                className="rounded-circle d-flex align-items-center justify-content-center border shadow-sm"
                                style={{ width: '50px', height: '50px', fontSize: '24px', backgroundColor: 'var(--vz-light)', borderColor: 'var(--vz-border-color)' }}
                                onClick={() => setCount(count + 1)}
                            >
                                +
                            </Button>
                        </div>
                    </div>

                    <div className="bg-light p-4 rounded-4 shadow-sm">
                        <label className="text-muted fw-bold text-uppercase tracking-wider mb-2 d-block" style={{ fontSize: '10px' }}>Space Prefix</label>
                        <Input
                            className="form-control-lg border-0 bg-card-theme shadow-sm fw-semibold"
                            placeholder="e.g. Table, Office, Room..."
                            value={prefix}
                            onChange={(e) => setPrefix(e.target.value)}
                            style={{ borderRadius: '12px' }}
                        />
                        <p className="text-muted small mt-2 mb-0" style={{ fontSize: '11px' }}>
                            Spaces will be named: <strong>{prefix} 1, {prefix} 2...</strong>
                        </p>
                    </div>
                </div>
            </div>

            <div className="p-4 border-top bg-card-theme sticky-bottom">
                <Button
                    block
                    size="lg"
                    className="w-100 rounded-pill py-3 shadow-lg fw-bold text-white border-0"
                    style={{ backgroundColor: THEME_COLOR }}
                    onClick={handleConfirm}
                    disabled={count <= 0}
                >
                    Next Step
                </Button>
            </div>
        </div>
    );
};

const MenuLinkView = ({ onBack, onConfirm, siteName, menus }) => {
    const [linkAll, setLinkAll] = useState(true);
    const [selectedMenuIds, setSelectedMenuIds] = useState([]);

    useEffect(() => {
        if (linkAll) {
            setSelectedMenuIds(menus.map(m => m.menu_id));
        } else {
            setSelectedMenuIds([]);
        }
    }, [linkAll, menus]);

    const toggleMenu = (id) => {
        if (linkAll) setLinkAll(false);
        setSelectedMenuIds(prev => {
            if (prev.includes(id)) {
                return prev.filter(mid => mid !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    return (
        <div className="h-100 d-flex flex-column bg-card-theme rounded-4 overflow-hidden">
            <div className="p-3 border-bottom d-flex align-items-center justify-content-between bg-card-theme sticky-top">
                <div className="d-flex align-items-center gap-3">
                    <Button color="link" className="text-muted p-0 me-2" onClick={onBack}><ArrowLeft size={20} /></Button>
                    <div>
                        <h5 className="fw-bold mb-0">Link Menus</h5>
                        <p className="text-muted small mb-0">{siteName}</p>
                    </div>
                </div>
            </div>

            <div className="flex-grow-1 overflow-auto p-4 custom-scroll">
                <div className="max-w-md mx-auto">
                    <h6 className="fw-bold text-center mb-4" style={{ color: THEME_COLOR }}>
                        Do you want to link the new spaces to all menu sections?
                    </h6>

                    <div
                        className={`card mb-3 cursor-pointer border-2 transition-all ${linkAll ? '' : 'bg-card-theme'}`}
                        style={{ backgroundColor: linkAll ? THEME_COLOR : 'var(--vz-border-color, #eee)', borderColor: linkAll ? THEME_COLOR : 'var(--vz-border-color, #eee)' }}
                        onClick={() => setLinkAll(true)}
                    >
                        <div className="card-body d-flex align-items-center gap-3">
                            <div className={`rounded-circle border d-flex align-items-center justify-content-center ${linkAll ? 'text-white ' : 'bg-card-theme'}`}
                                style={{ width: '24px', height: '24px', backgroundColor: linkAll ? THEME_COLOR : undefined, borderColor: linkAll ? THEME_COLOR : 'var(--vz-border-color)' }}>
                                {linkAll && <Check size={14} />}
                            </div>
                            <div >
                                <h6 style={{ color: linkAll ? "white" : 'var(--vz-border-color)' }} className="mb-0 fw-bold">Link All Sections</h6>
                                <p className={` small mb-0 ${linkAll ? "text-white" : "text-muted"}`}>Automatically link current and future standard menus</p>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-3 mb-3">
                        <hr className="flex-grow-1 opacity-25" />
                        <span className="text-muted small text-uppercase fw-bold">OR Select Specific</span>
                        <hr className="flex-grow-1 opacity-25" />
                    </div>

                    <div className="d-flex flex-column gap-2 mb-4">
                        {menus.map(menu => {
                            const isSelected = selectedMenuIds.includes(menu.menu_id);
                            return (
                                <div
                                    key={menu.menu_id}
                                    className={`card border cursor-pointer transition-all ${isSelected ? 'shadow-sm' : ''}`}
                                    style={{ borderColor: isSelected ? THEME_COLOR : 'var(--vz-border-color, #eee)', borderWidth: isSelected ? '1px' : '1px' }}
                                    onClick={() => toggleMenu(menu.menu_id)}
                                >
                                    <div className="card-body p-3 d-flex align-items-center gap-3">
                                        <div className={`rounded-circle border d-flex align-items-center justify-content-center transition-all ${isSelected ? 'text-white' : 'bg-card-theme'}`}
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                backgroundColor: isSelected ? THEME_COLOR : undefined,
                                                borderColor: isSelected ? THEME_COLOR : 'var(--vz-border-color, #ddd)'
                                            }}
                                        >
                                            {isSelected && <Check size={12} />}
                                        </div>
                                        <span className={`fw-semibold ${isSelected ? 'text-body' : 'text-muted'}`}>{menu.name}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="p-4 border-top bg-card-theme sticky-bottom">
                <Button
                    block
                    size="lg"
                    className="w-100 rounded-pill py-3 shadow-lg fw-bold text-white border-0"
                    style={{ backgroundColor: THEME_COLOR }}
                    onClick={() => onConfirm(selectedMenuIds)}
                    disabled={!linkAll && selectedMenuIds.length === 0}
                >
                    Confirm & Create Spaces
                </Button>
            </div>
        </div>
    );
};

const KitchenLinkView = ({ onBack, onConfirm, siteName, kitchens }) => {
    const [selectedId, setSelectedId] = useState(kitchens[0]?.kitchen_id);

    return (
        <div className="h-100 d-flex flex-column bg-card-theme rounded-4 overflow-hidden">
            <div className="p-3 border-bottom d-flex align-items-center justify-content-between bg-card-theme sticky-top">
                <div className="d-flex align-items-center gap-3">
                    <Button color="link" className="text-muted p-0 me-2" onClick={onBack}><ArrowLeft size={20} /></Button>
                    <div>
                        <h5 className="fw-bold mb-0">Select Station</h5>
                        <p className="text-muted small mb-0">{siteName}</p>
                    </div>
                </div>
            </div>

            <div className="flex-grow-1 overflow-auto p-4 custom-scroll">
                <div className="max-w-md mx-auto">
                    <h6 className="fw-bold text-center mb-4" style={{ color: THEME_COLOR }}>
                        Which station should receive orders from these new spaces?
                    </h6>

                    <div className="d-flex flex-column gap-2 mb-4">
                        {kitchens.map(kitchen => {
                            const isSelected = selectedId === kitchen.kitchen_id;
                            return (
                                <div
                                    key={kitchen.kitchen_id}
                                    className={`card border cursor-pointer transition-all ${isSelected ? 'shadow-sm' : ''}`}
                                    style={{ borderColor: isSelected ? THEME_COLOR : 'var(--vz-border-color, #eee)', borderWidth: isSelected ? '1px' : '1px' }}
                                    onClick={() => setSelectedId(kitchen.kitchen_id)}
                                >
                                    <div className="card-body p-3 d-flex align-items-center gap-3">
                                        <div className={`rounded-circle border d-flex align-items-center justify-content-center transition-all ${isSelected ? 'text-white' : 'bg-card-theme'}`}
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                backgroundColor: isSelected ? THEME_COLOR : undefined,
                                                borderColor: isSelected ? THEME_COLOR : 'var(--vz-border-color, #ddd)'
                                            }}
                                        >
                                            {isSelected && <Check size={12} />}
                                        </div>
                                        <span className={`fw-semibold ${isSelected ? 'text-body' : 'text-muted'}`}>{kitchen.name}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="p-4 border-top bg-card-theme sticky-bottom">
                <Button
                    block
                    size="lg"
                    className="w-100 rounded-pill py-3 shadow-lg fw-bold text-white border-0"
                    style={{ backgroundColor: THEME_COLOR }}
                    onClick={() => onConfirm(selectedId)}
                >
                    Link Station & Finish
                </Button>
            </div>
        </div>
    );
};

const ManageSpacesView = ({ siteId, onClose }) => {
    const { t } = useTranslation();
    const { sites } = useSiteStore();
    const { getSpaces, isLoading: loadingExisting, createSpace, updateSpace, deleteSpace } = useSpaceStore();
    const { getSiteMenuFull } = useMenuStore();
    const { getKitchensBySite } = useKitchenStore();
    const [hasChanged, setHasChanged] = useState(false);
    const [siteInfo, setSiteInfo] = useState(null);
    const [existingSpaces, setExistingSpaces] = useState([]);
    const [newSpaces, setNewSpaces] = useState([]); // Array of objects { name: string, menu_ids: [int] }
    const [viewMode, setViewMode] = useState('list'); // 'list' | 'generate' | 'link_menus'
    const [showExitConfirm, setShowExitConfirm] = useState(false);

    // Temporary state for generation flow
    const [generatedNames, setGeneratedNames] = useState([]);

    const [siteMenus, setSiteMenus] = useState([]); // Available menus for linking
    const [siteKitchens, setSiteKitchens] = useState([]); // Available kitchens for linking
    const [kitchenId, setKitchenId] = useState(null); // Default kitchen ID
    const [tempMenuIds, setTempMenuIds] = useState([]); // Temp storage for menu selection during flow

    // Selection & Editing
    const [selectedExisting, setSelectedExisting] = useState([]);
    const [selectedNew, setSelectedNew] = useState([]);
    const [editingExistingId, setEditingExistingId] = useState(null);
    const [editingNewIndex, setEditingNewIndex] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [updatingId, setUpdatingId] = useState(null); // For loading state during individual update
    const [isSaving, setIsSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, target: null, type: null });

    const editInputRef = useRef(null);

    // Initial Fetch
    useEffect(() => {
        const site = sites.find(s => s.site_id === siteId);
        if (site) setSiteInfo(site);

        const fetchSpaces = async () => {
            if (siteId) {
                const spaces = await getSpaces(siteId, { page: 1, perPage: 100 });
                setExistingSpaces(spaces || []);
            }
        };

        const fetchMenus = async () => {
            if (siteId) {
                const data = await getSiteMenuFull(siteId);
                if (data && data.menus) {
                    setSiteMenus(data.menus);
                }
            }
        }
        // this needs to change, if theres more than 1 kitchen then we let the user choose which kitchen will receive the orders
        const fetchKitchens = async () => {
            if (siteId) {
                const response = await getKitchensBySite(siteId, { page: 1, perPage: 100 });

                if (response?.kitchens?.length > 0) {
                    setSiteKitchens(response.kitchens);
                    // If only one kitchen, set it as default
                    if (response.kitchens.length === 1) {
                        setKitchenId(response.kitchens[0].kitchen_id);
                    }
                }
            }
        }

        fetchSpaces();
        fetchMenus();
        fetchKitchens();
    }, [siteId, sites]);

    // --- Actions ---

    // Step 1: Generate Names -> Go to Link Menus
    const handleGenerateStep1 = (names) => {
        setGeneratedNames(names);
        setViewMode('link_menus');
    };

    // Step 2: Confirm Menus -> Check if multiple kitchens, otherwise finish
    const handleGenerateConfirm = (selectedMenuIds) => {
        if (siteKitchens.length > 1) {
            setTempMenuIds(selectedMenuIds);
            setViewMode('link_kitchen');
        } else {
            // Only one kitchen or default logic
            setHasChanged(true);
            const spacesToAdd = generatedNames.map(name => ({
                name,
                menu_ids: selectedMenuIds,
                kitchen_id: kitchenId || (siteKitchens[0]?.kitchen_id)
            }));

            setNewSpaces(prev => [...prev, ...spacesToAdd]);
            setGeneratedNames([]);
            setViewMode('list');
        }
    };

    // Step 3: Confirm Kitchen -> Add to New Spaces List
    const handleKitchenConfirm = (selectedKitchenId) => {
        setHasChanged(true);
        const spacesToAdd = generatedNames.map(name => ({
            name,
            menu_ids: tempMenuIds,
            kitchen_id: selectedKitchenId
        }));

        setNewSpaces(prev => [...prev, ...spacesToAdd]);
        setGeneratedNames([]);
        setTempMenuIds([]);
        setViewMode('list');
    };

    const handleSaveNewSpaces = async () => {
        if (newSpaces.length === 0) return;
        setIsSaving(true);
        try {
            // const userPlan = 
            //from the user plan we decide on what to set service level to

            // Create spaces sequentially or parallel
            let successCount = 0;
            for (const space of newSpaces) {
                // Use space.kitchen_id if it exists, otherwise fall back to global kitchenId
                await createSpace(siteId, { name: space.name, service_level: 3, type: "RESTAURANT" }, space.kitchen_id || kitchenId, space.menu_ids);
                successCount++;
            }

            if (successCount > 0) {
                toast.success(`${successCount} spaces created successfully`);
                setNewSpaces([]);
                // Refresh existing spaces
                const spaces = await getSpaces(siteId, { page: 1, perPage: 100 });
                setExistingSpaces(spaces || []);
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to create some spaces");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteNew = (indices) => {
        setHasChanged(true);
        setNewSpaces(prev => prev.filter((_, i) => !indices.includes(i)));
        setSelectedNew([]);
    };

    const handleDeleteExisting = async (ids) => {
        setHasChanged(true);
        const idArray = Array.isArray(ids) ? ids : [ids];
        if (idArray.length === 0) return;

        let successCount = 0;
        for (const id of idArray) {
            const success = await deleteSpace(id, siteId);
            if (success) successCount++;
        }

        if (successCount > 0) {
            toast.success(`${successCount} spaces deleted`);
            const spaces = await getSpaces(siteId, { page: 1, perPage: 100 });
            setExistingSpaces(spaces || []);
            setSelectedExisting([]);
        }
    };

    const updateExistingSpace = async (id, name) => {
        if (!name.trim()) return;
        setUpdatingId(id);
        const success = await updateSpace(id, { name }, siteId);
        if (success) {
            setExistingSpaces(prev => prev.map(s => s.space_id === id ? { ...s, name } : s));
        }
        setUpdatingId(null);
        setEditingExistingId(null);
    };

    const updateNewSpace = (index, name) => {
        setHasChanged(true);
        if (!name.trim()) return;
        setNewSpaces(prev => {
            const copy = [...prev];
            // Update name property since it is now an object
            copy[index] = { ...copy[index], name };
            return copy;
        });
        setEditingNewIndex(null);
    };

    // --- Selection Logic ---
    const toggleSelectionExisting = (id) => {
        setHasChanged(true);
        setSelectedExisting(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const toggleSelectionNew = (index) => {
        setHasChanged(true);
        setSelectedNew(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]);
    };

    const handleSelectAll = (type) => {
        if (type === 'existing') {
            if (selectedExisting.length === existingSpaces.length) setSelectedExisting([]);
            else setSelectedExisting(existingSpaces.map(s => s.space_id));
        } else {
            if (selectedNew.length === newSpaces.length) setSelectedNew([]);
            else setSelectedNew(newSpaces.map((_, i) => i));
        }
    };

    // --- Editing UI Handlers ---
    const startEditingExisting = (id, currentName, e) => {
        e.stopPropagation();
        setEditingExistingId(id);
        setEditValue(currentName);
        setTimeout(() => editInputRef.current?.focus(), 50);
    };

    const startEditingNew = (index, currentName, e) => {
        e.stopPropagation();
        setEditingNewIndex(index);
        setEditValue(currentName);
        setTimeout(() => editInputRef.current?.focus(), 50);
    };

    const handleKeyDown = (e, type, idOrIndex) => {
        if (e.key === 'Enter') {
            if (type === 'existing') updateExistingSpace(idOrIndex, editValue);
            else updateNewSpace(idOrIndex, editValue);
        }
        if (e.key === 'Escape') {
            setEditingExistingId(null);
            setEditingNewIndex(null);
        }
    };

    // Delete Modal Helpers
    const requestDelete = (target, type) => {
        setConfirmDelete({ isOpen: true, target, type });
    };

    const executeDelete = () => {
        const { target, type } = confirmDelete;
        if (type === 'existing') handleDeleteExisting(target);
        else handleDeleteNew(target);
        setConfirmDelete({ isOpen: false, target: null, type: null });
    };

    const attemptClose = () => {
        if (hasChanged) {
            setShowExitConfirm(true);
        } else {
            onClose();
        }
    };
    return (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-body-theme d-flex flex-column manage-spaces-overlay">
            {/* --- Header --- */}
            <div className="manage-spaces-header px-4 py-3 d-flex align-items-center justify-content-between shadow-sm">
                <div className="d-flex align-items-center gap-3">
                    <button onClick={() => {
                        attemptClose();
                    }} className="btn btn-light rounded-circle p-2 hover-bg-silver text-muted border">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h5 className="mb-0 fw-bold">{t("Manage Spaces")}</h5>
                        <p className="text-muted small mb-0">{siteInfo?.name}</p>
                    </div>
                </div>
                {newSpaces.length > 0 && (
                    <button
                        onClick={handleSaveNewSpaces}
                        disabled={isSaving}
                        className="btn btn-primary d-flex align-items-center gap-2 px-4 rounded-pill shadow-sm"
                        style={{ backgroundColor: THEME_COLOR, borderColor: THEME_COLOR }}
                    >
                        <Save size={18} />
                        {isSaving ? t('Saving...') : t('Save New Spaces')}
                    </button>
                )}
            </div>

            {/* --- Main Content --- */}
            <div className="flex-grow-1 overflow-hidden bg-body-theme position-relative">
                <AnimatePresence mode="wait">
                    {/* Mode: GENERATE STEP 1 */}
                    {viewMode === 'generate' && (
                        <motion.div
                            key="generate"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="h-100 p-4"
                        >
                            <div className="h-100 max-w-2xl mx-auto shadow-lg rounded-4 overflow-hidden">
                                <GenerationView siteName={siteInfo?.name} onCancel={() => setViewMode('list')} onConfirm={handleGenerateStep1} />
                            </div>
                        </motion.div>
                    )}

                    {/* Mode: GENERATE STEP 2 (LINK MENUS) */}
                    {viewMode === 'link_menus' && (
                        <motion.div
                            key="link_menus"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="h-100 p-4"
                        >
                            <div className="h-100 max-w-2xl mx-auto shadow-lg rounded-4 overflow-hidden">
                                <MenuLinkView
                                    siteName={siteInfo?.name}
                                    menus={siteMenus}
                                    onBack={() => setViewMode('list')} // Or back to 'generate' if preferred
                                    onConfirm={handleGenerateConfirm}
                                />
                            </div>
                        </motion.div>
                    )}

                    {/* Mode: LINK KITCHEN (Step 3) */}
                    {viewMode === 'link_kitchen' && (
                        <motion.div
                            key="link_kitchen"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="h-100 p-4"
                        >
                            <div className="h-100 max-w-2xl mx-auto shadow-lg rounded-4 overflow-hidden">
                                <KitchenLinkView
                                    siteName={siteInfo?.name}
                                    kitchens={siteKitchens}
                                    onBack={() => setViewMode('link_menus')}
                                    onConfirm={handleKitchenConfirm}
                                />
                            </div>
                        </motion.div>
                    )}

                    {/* Mode: LIST */}
                    {viewMode === 'list' && (
                        <motion.div
                            key="list"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="h-100 overflow-auto custom-scroll p-4"
                        >
                            <div className="max-w-4xl mx-auto">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <h6 className="mb-0 fw-bold text-muted text-uppercase small">{t("All Spaces")}</h6>
                                        <Badge color="secondary" className="bg-opacity-10 text-dark">
                                            {existingSpaces.length + newSpaces.length}
                                        </Badge>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Button
                                            outline
                                            className="rounded-pill px-3 border-dashed"
                                            style={{ borderColor: THEME_COLOR, color: THEME_COLOR }}
                                            onClick={() => setViewMode('generate')}
                                        >
                                            <Plus size={14} className="me-1" /> {t("Add Spaces")}
                                        </Button>
                                    </div>
                                </div>

                                {/* Combined List: New Spaces First, then Existing */}
                                <div className="d-flex flex-wrap gap-2">
                                    {/* 1. New Spaces */}
                                    {newSpaces.map((spaceObj, i) => (
                                        <div key={`new-${i}`}>
                                            {editingNewIndex === i ? (
                                                <div className="bg-card-theme border rounded-4 p-2 px-3 shadow-sm d-flex align-items-center gap-2" style={{ width: '200px' }}>
                                                    <Input
                                                        innerRef={editInputRef}
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        onBlur={() => updateNewSpace(i, editValue)}
                                                        onKeyDown={(e) => handleKeyDown(e, 'new', i)}
                                                        className="form-control-sm border-0 p-0 m-0 fw-bold fs-6 shadow-none flex-grow-1"
                                                        style={{ background: 'transparent' }}
                                                        autoFocus
                                                    />
                                                </div>
                                            ) : (
                                                <SpaceCard
                                                    name={spaceObj.name} /** Object has name property now */
                                                    isNew={true}
                                                    isSelected={selectedNew.includes(i)}
                                                    onToggle={() => toggleSelectionNew(i)}
                                                    onEdit={(e) => startEditingNew(i, spaceObj.name, e)}
                                                    onDelete={(e) => { e.stopPropagation(); requestDelete([i], 'new'); }}
                                                />
                                            )}
                                        </div>
                                    ))}

                                    {/* 2. Existing Spaces */}
                                    {existingSpaces.map((space) => (
                                        <div key={space.space_id}>
                                            {editingExistingId === space.space_id ? (
                                                <div className="bg-card-theme border rounded-4 p-2 px-3 shadow-sm d-flex align-items-center gap-2" style={{ width: '200px' }}>
                                                    <Input
                                                        innerRef={editInputRef}
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        onBlur={() => updateExistingSpace(space.space_id, editValue)}
                                                        onKeyDown={(e) => handleKeyDown(e, 'existing', space.space_id)}
                                                        className="form-control-sm border-0 p-0 m-0 fw-bold fs-6 shadow-none flex-grow-1"
                                                        style={{ background: 'transparent' }}
                                                        autoFocus
                                                    />
                                                </div>
                                            ) : (
                                                <SpaceCard
                                                    name={space.name}
                                                    loading={updatingId === space.space_id}
                                                    isSelected={selectedExisting.includes(space.space_id)}
                                                    onToggle={() => toggleSelectionExisting(space.space_id)}
                                                    onEdit={(e) => startEditingExisting(space.space_id, space.name, e)}
                                                    onDelete={(e) => { e.stopPropagation(); requestDelete([space.space_id], 'existing'); }}
                                                />
                                            )}
                                        </div>
                                    ))}

                                    {existingSpaces.length === 0 && newSpaces.length === 0 && (
                                        <div className="w-100 text-center py-5 bg-card-theme rounded-4 border border-dashed">
                                            <div className="mb-3">
                                                <LayoutGrid size={32} className="text-muted opacity-25" />
                                            </div>
                                            <p className="text-muted mb-3">{t("No spaces found for this branch")}</p>
                                            <Button
                                                className="rounded-pill px-4 text-white border-0"
                                                style={{ backgroundColor: THEME_COLOR }}
                                                onClick={() => setViewMode('generate')}
                                            >
                                                {t("Create First Spaces")}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            {/* Change Tracking Exit Confirmation Modal */}
            < Modal isOpen={showExitConfirm} toggle={() => setShowExitConfirm(false)} centered size="sm" >
                <div className="p-4 text-center">
                    <div className="mx-auto bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                        <AlertTriangle className="text-warning" size={28} />
                    </div>
                    <h5 className="fw-bold mb-2">{t("Unsaved Changes")}</h5>
                    <p className="text-muted small mb-4">
                        {t("You have unsaved changes. Are you sure you want to discard them and exit?")}
                    </p>
                    <div className="d-flex gap-2 justify-content-center">
                        <Button color="light" className="border px-4" onClick={() => setShowExitConfirm(false)}>{t("Cancel")}</Button>
                        <Button color="danger" className="px-4" onClick={() => onClose(false)}>{t("Exit without Saving")}</Button>
                    </div>
                </div>
            </Modal >
            {/* Delete Confirmation Modal */}
            <Modal isOpen={confirmDelete.isOpen} toggle={() => setConfirmDelete({ ...confirmDelete, isOpen: !confirmDelete.isOpen })} centered size="sm">
                <div className="p-4 text-center">
                    <div className="mx-auto bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                        <Trash2 className="text-danger" size={24} />
                    </div>
                    <h5 className="fw-bold mb-2">{t("Delete Space?")}</h5>
                    <p className="text-muted small mb-4">
                        {t("Are you sure you want to delete")} {Array.isArray(confirmDelete.target) ? `${confirmDelete.target.length} ` : ''} {t("space(s)?")}
                        <br />
                        {t("This action cannot be undone.")}
                    </p>
                    <div className="d-flex gap-2 justify-content-center">
                        <Button color="light" className="border px-4" onClick={() => setConfirmDelete({ ...confirmDelete, isOpen: false })}>{t("Cancel")}</Button>
                        <Button color="danger" className="px-4" onClick={executeDelete}>{t("Delete")}</Button>
                    </div>
                </div>
            </Modal>

        </div>
    );
};

export default ManageSpacesView;
